<script lang="ts">
    // packages
    import { geoIdentity, geoPath } from 'd3-geo';
    import { pointer } from 'd3-selection';
    import { intcomma } from 'journalize';
    import { onMount } from 'svelte';
  
    // components
    import Tooltip from '../Tooltip.svelte';
    import TooltipRow from '../TooltipRow.svelte';
  
    // utils
    import apstate from '../../utils/apstate'
    import { createHatchFill, getColor, NEUTRAL, TIE } from '../../colors';
    import createSearchIndex from '../../utils/create-search-index';
    import calculateHeightRatio from '../../utils/calculate-height-ratio';
    import scaleCanvas from '../../utils/scale-canvas';
    import computeKeyColorClass from "../../utils/compute-key-color-class";
    import { formatPercent } from '../../utils/format-percent';
    import parties from '../../utils/parties';

    // types
    import type { Feature, FeatureCollection, MultiLineString } from 'geojson';
    import type {
      CandidateInterface,
      OfficeRaceInterface,
    } from '../../interfaces';
  
    // props
    export let geography: {
      collection: FeatureCollection;
      innerlines: MultiLineString;
    };
    export let globalInnerlines: MultiLineString | undefined;
    export let data: Map<string, OfficeRaceInterface>;
    export let divisionType = 'state';
    // export let subunits: Map<string, SubunitInterface>;
    export let electoralVoteDistrictRaces: OfficeRaceInterface[] | undefined;
    export let specialRaces: OfficeRaceInterface[] | undefined;

    // state
    let width: number;
    let height: number;
    let container: HTMLDivElement;
    let canvas: HTMLCanvasElement;
    let context: CanvasRenderingContext2D;
    let svg: SVGSVGElement;
    let highlightedFeature: Feature | undefined;
    let activeRace: OfficeRaceInterface;
    let x: number;
    let y: number;
    $: findPolygon = createSearchIndex(geography.collection.features);

    // constants
    const dpi = window.devicePixelRatio || 1;
    const margin = 5;
    const projection = geoIdentity().reflectY(true);
    const path = geoPath(projection);
    const svgPath = geoPath(projection);
    const heightRatio = calculateHeightRatio(geography.collection);
    const electoralVoteDistrictStates = [
      { name: "Nebraska", ap: "Neb." },
      { name: "Maine", ap: "Maine" },
    ]

    onMount(() => {
      context = canvas.getContext('2d') as CanvasRenderingContext2D;
      path.context(context);
      context.scale(dpi, dpi);
      context.translate(margin, margin);
      getBounds();
      render(geography.collection, geography.innerlines, globalInnerlines, data);
    });

    function render(
      geography: FeatureCollection,
      innerlines: MultiLineString,
      globalInnerlines: MultiLineString | undefined,
      lookup: Map<string, OfficeRaceInterface>
    ) {
      if (!context) return;
  
      context.clearRect(0, 0, width, height);
  
      context.lineCap = 'round';
      context.lineJoin = 'round';
  
      for (let idx = 0; idx < geography.features.length; idx++) {
        const feature = geography.features[idx];
        const fillStyle = getFillStyle(feature, lookup);
  
        context.beginPath();
        path(feature);
        context.fillStyle = fillStyle;
        context.fill();
        context.closePath();
      }
  
      context.beginPath();
      path(innerlines);
      context.strokeStyle = 'rgba(255, 255, 255, 0.65)';
      context.lineWidth = 0.6;
      context.stroke();
      context.closePath();
  
      if (globalInnerlines) {
        context.beginPath();
        path(globalInnerlines);
        context.strokeStyle = 'rgba(255, 255, 255, 0.65)';
        context.lineWidth = 1;
        context.stroke();
        context.closePath();
      }
    }
  
    function getFillStyle(
      feature: Feature,
      lookup: Map<string, OfficeRaceInterface>
    ) {
      const code: string = feature.properties!.code;
      const subunit = lookup.get(code);
  
      let color: string;
      let winner: CandidateInterface | undefined;
  
      // if (
      //   (divisionType === 'state' || divisionType === 'district') &&
      //   subunit &&
      //   subunit.id !== '12588'
      // ) {
      //   winner = subunit.candidates.find((candidate) => candidate.is_winner);
      // }
      // winner = subunit.candidates.find((candidate) => candidate.is_winner);

      // Check for winner first
      if (
        (divisionType === 'state' || divisionType === 'district') &&
        subunit
      ) {
        if (subunit && subunit.candidates && subunit.candidates.find((candidate) => candidate.is_winner) !== undefined) {
          winner = subunit.candidates.find((candidate) => candidate.is_winner)
        }
      }

      if (!subunit) {
        color = NEUTRAL;
      } else if (subunit.total_votes <= 0 && !winner) {
        winner = subunit.candidates.find((candidate) => candidate.is_winner);
        color = NEUTRAL;
        if (winner){
          color = getColor(winner.party)
        }
      } else {
        const leader = winner || subunit.candidates[0];
        const second = subunit.candidates[1] ? subunit.candidates[1] : null;
  
        if (!winner && second && leader.votes === second.votes) {
          color = TIE;
        } else {
          const partyColor = getColor(leader.party);
  
          color =
            divisionType === 'county'
              ? partyColor
              : subunit.id !== '12588' && leader.is_winner  // take off?
              ? partyColor
              : createHatchFill(partyColor);
        }
      }

      return color;
    }
  
    function getBounds() {
      const bounds = container.getBoundingClientRect();
  
      width = bounds.width;
      height = width * heightRatio;
  
      projection.fitSize(
        [width - margin - margin, height - margin - margin],
        geography.collection
      );

      // svg
      svg.setAttribute('width', width.toString());
      svg.setAttribute('height', height.toString());
      svg.setAttribute('viewBox', `0 0 ${width} ${height}`);
  
      // canvas
      scaleCanvas(context, width, height, margin);
    }
  
    function onResize() {
      getBounds();
      render(geography.collection, geography.innerlines, globalInnerlines, data);
    }
  
    $: render(geography.collection, geography.innerlines, globalInnerlines, data);

    function onMouseMove(e: MouseEvent) {
      const coord = pointer(e);

      // invert the coords using our projection
      const coords = projection.invert([
        coord[0] - margin,
        coord[1] - margin,
      ]) as [number, number];
  
      // find it
      const active = findPolygon(coords);
  
      x = e.clientX;
      y = e.clientY;
  
      if (active) {
        highlightedFeature = active;
        const code = active.properties.code;
        const subunit = data.get(code);
        const race = getActiveRace(subunit)

        if (race) {
          activeRace = race
        } else {
          activeRace = {
            name: highlightedFeature.properties.name,
            noRace: true,
          };
        }

      } else {
        highlightedFeature = undefined;
        activeRace = undefined;
      }
    }
  
    function onMouseOut() {
      highlightedFeature = undefined;
      activeRace = undefined;
    }

    // Helper function
    function getActiveRace(subunit, division) {
      const activeRaceDivisionType = division || divisionType

      let race = null;
      if (subunit) {
        const candidates = subunit.candidates;

        const results = candidates.slice(0, 3).map((candidate) => {
          return {
            ap_id: candidate.ap_id,
            name: candidate.family_name,
            party: candidate.party,
            votes: candidate.votes,
            votePercent: formatPercent(
              candidate.votes,
              subunit.total_votes,
              subunit.id === '12301' ? 3 : 1
            ),
            isWinner: candidate.is_winner,
          };
        });

        let name = '';

        switch (activeRaceDivisionType) {
          case 'state':
            name = subunit.name;
            break;
          case 'county':
            name = `${subunit.name} (${subunit.state})`;
            break;
          case 'district':
            name = `${subunit.name} District ${subunit.seat_number}`;
            break;
        }

        race = {
          name,
          candidates: results,
          totalVotes: subunit.total_votes,
          precinctsTotal: subunit.precincts_total,
          precinctsReporting: subunit.precincts_reporting,
          isSpecialElection: subunit.is_special_election,
          electorsTotal: subunit.electors_total,
          eevp: subunit.eevp,
        };
      }
      return race
    }

    function onMouseMoveExtraRace(e: MouseEvent, race: OfficeRaceInterface, raceType: String) {
      if (e && race) {
        e.target.classList.add("hovered")
        x = e.clientX
        y = e.clientY
        activeRace = getActiveRace(race, raceType)
      }
    }

    function onMouseOutExtraRace(e: MouseEvent) {
      if (e.target) {
        e.target.classList.remove("hovered")
      }
      onMouseOut()
    }

  function generateCandidateResultsSentence(t) {
    if (!t.noRace) {
      let firstSentence = `${t.name} has ${t.eevp}% of expected vote in${t.isSpecialElection ? ' its special election.' : '.'}`;
      if (t.eevp === 0) {
        return firstSentence;
      } else {
      let tmp = [];
      let tmp_sentence = '';
      let tmp_winner_sentence = '';
      (t.candidates).forEach((x, i)=>{
        if (x.isWinner) {
          return tmp_winner_sentence = `The race has been called for ${x.name} (${parties[x.party] ? parties[x.party].label : (x.party).toUpperCase()}) with ${x.votePercent}% of the votes so far or ${x.votes} votes.`;
        } else  {
          tmp_sentence = `${x.name} (${parties[x.party] ? parties[x.party].label : (x.party).toUpperCase()}) has ${x.votePercent}% of votes, with a total of ${x.votes} votes.`;
          tmp.push(tmp_sentence)
        }
      })
      let candidateSentences = tmp.join(' ');

      let finalSentence = `${firstSentence} ${tmp_winner_sentence ? tmp_winner_sentence + " " + candidateSentences : candidateSentences}`;
      return finalSentence;
      }
    } else {
      return `No race in ${t.name}.`
    }
  }

  let tooltipAriaLabel;
  


  $: if (activeRace) {
    tooltipAriaLabel = `${generateCandidateResultsSentence(activeRace)}`
    
  }

  </script>

  <svelte:window on:resize|passive={onResize} />

  <div
    class="box-border position-relative"
    bind:this={container}
    on:mousemove={onMouseMove}
    on:mouseout={onMouseOut}
    on:blur={onMouseOut}
    >
    
    <svg
      style="position: relative; top: 0; left: 0; z-index: 2;"
      bind:this={svg}
      preserveAspectRatio="xMidYMid"
      stroke-linecap="round"
       
      stroke-linejoin="round"><g
        pointer-events="none"
        transform={`translate(${margin},${margin})`}>
        {#if highlightedFeature}
          <path
            stroke="#292929"
            stroke-width="2"
            fill="none"
            d={svgPath(highlightedFeature)} />
        {/if}
      </g></svg>
    <canvas bind:this={canvas} class="position-absolute top-0 left-0" />
  </div>

  {#if activeRace}
    <Tooltip {x} {y}>
      <div class="contest" role="tooltip" aria-live="polite" aria-label="{tooltipAriaLabel}">
        {#if activeRace.noRace}
          <table class="contest__candidates contest__candidates--tooltip">
            <caption>
              <p class="contest__title">No race in {activeRace.name}</p>
            </caption>
          </table>
        {:else}
          <table class="contest__candidates contest__candidates--tooltip">
            <caption>
              <p class="contest__title text-left">
                <span class="text-bold">{activeRace.name}</span>                 
                {#if activeRace.electorsTotal && (divisionType == 'state')}
                ({(activeRace.electorsTotal == 1)
                    ? `${activeRace.electorsTotal } electoral vote`
                    : `${activeRace.electorsTotal } electoral votes` })
                {/if}
                {#if activeRace.isSpecialElection}
                (Special election)
              {/if}
              </p>
              <div class="contest__metrics visually-hidden">
                <p class="contest__precincts visually-hidden">
                  {#if activeRace.eevp >= 0 && (activeRace.eevp !== null)}
                    {activeRace.eevp}% of expected vote in
                  {/if}
                </p>
              </div>
            </caption>
            <thead>
              <tr>
                <th
                  scope="col"
                  class="contest__candidate-header whitespace-no-wrap">
                  &nbsp;
                </th>
                <th
                  scope="col"
                  class="contest__candidate-header whitespace-no-wrap text-left ml-0 px-n0">
                  Candidate
                </th>
                <!-- <th
                  scope="col"
                  class="contest__candidate-header whitespace-no-wrap">
                  Party
                </th> -->
                <th
                  scope="col"
                  class="contest__candidate-header text-left whitespace-no-wrap total text-grey">
                  Votes
                </th>
                <th
                scope="col"
                class="contest__candidate-header text-right whitespace-no-wrap">
                %
              </th>
              </tr>
            </thead>
            <tbody>
              {#if activeRace.candidates.length > 0}
                {#each activeRace.candidates as candidate (candidate.ap_id)}
                  <TooltipRow 
                    name={candidate.name}
                    party={candidate.party}
                    votes={candidate.votes}
                    votePercent={candidate.votePercent}
                    isWinner={divisionType === 'county' ? false : candidate.isWinner}
                    includeParty=true
                    color=""
                    race="congress"
                      />
                {/each}
              {:else}
                <tr>
                  <th
                    class="candidate__cell candidate__cell-empty"
                    scope="col"
                    colSpan={4}>
                    No results
                  </th>
                </tr>
              {/if}
            </tbody>
          </table>
        {/if}
      </div>
      {#if !activeRace.noRace}
      <div class="contest__metrics" aria-hidden="true">
        <p class="contest__precincts text-right">
          {#if (activeRace.eevp >= 0) && (activeRace.eevp !== null)}
            {activeRace.eevp}% of expected vote in
          {/if}
        </p>
      </div>
      {/if}
    </Tooltip>
  {/if}

  {#if specialRaces?.length}
    <div class="special-elections display-flex text-sans-serif text-xs">
      <div class="special-elections__box">
        <p class="special-elections__label text-left text-bold mb-n4">Special elections</p>
        <div class="">
          {#each specialRaces as r}
            <div
              class="special-elections__race display-flex"
              id="{ `special-elections-${r.code}` }"
              on:mousemove={ (e) => {
                const race = data.get(r.code)
                const raceType = divisionType
                onMouseMoveExtraRace(e, race, raceType)
              }}
              on:mouseout={onMouseOutExtraRace}
              on:blur={onMouseOutExtraRace}
            >
              <span class="m-0 mr-n3 key-color { computeKeyColorClass(r) }"></span>
              <span class="special-elections__race__label">{ divisionType == 'state' ? `${apstate.get(r.state_fips).ap || r.state_postal}` :`${r.state_postal}-${r.seat_number}` }</span>
            </div>
          {/each}
        </div>
      </div>
    </div>
  {/if}

  {#if electoralVoteDistrictRaces}
    <div class="district-electoral-votes text-sans-serif text-xs">
      <div class="district-electoral-votes__box">
        <p class="district-electoral-votes__label text-left text-bold mb-n4">By district</p>
        <table>
          <thead>
            <th scope="col" class="text-left p-2px" />
            <th scope="col" class="p-2px">1</th>
            <th scope="col" class="p-2px">2</th>
            <th scope="col" class="p-2px">3</th>
          </thead>
          <tbody>
            {#each electoralVoteDistrictStates as state}
              <tr>
                <th scope="row" class="text-medium text-left p-2px">{ state.ap }</th>
                {#each electoralVoteDistrictRaces.filter(r => r.state == state.name).sort((a,b) => (Number(a.seat_number) > Number(b.seat_number)) ? 1 : -1 ) as r }
                  <td class="p-2px district-electoral-votes__race"
                    on:mousemove={ (e) => {
                      const race = electoralVoteDistrictRaces.find(d => d.code == r.code && d.seat_number == r.seat_number)
                      const raceType = "district"
                      onMouseMoveExtraRace(e, race, raceType)
                    }}
                    on:mouseout={onMouseOutExtraRace}
                    on:blur={onMouseOutExtraRace}
                  ><span class="m-0 key-color { computeKeyColorClass(r) }"></span></td>
                {/each}
              </tr>
            {/each}
          </tbody>
        </table>
      </div>
    </div>
  {/if}